import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAffiliateSessionStore } from '../../store/affiliateSessionStore';
import { affiliateService } from '../../services/affiliateService';

type TaxFormType = 'W-9' | 'W-8BEN';

interface TaxFormPayload {
  formType: TaxFormType;
  name: string;
  businessName?: string;
  tin?: string; // SSN/EIN (digits only)
  country?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

const digitsOnly = (v: string) => v.replace(/\D+/g, '');

export const AffiliateTaxCompliance: React.FC = () => {
  const affiliateUserId = useAffiliateSessionStore((s) => s.affiliateUserId);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusLoading, setStatusLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<any>(null);
  const [form, setForm] = useState<TaxFormPayload>({
    formType: 'W-9',
    name: '',
    businessName: '',
    tin: '',
    country: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: ''
  });

  const maskedTin = useMemo(() => {
    const raw = digitsOnly(form.tin || '');
    if (!raw) return '';
    if (raw.length <= 4) return raw; // minimal
    return `${'*'.repeat(Math.max(0, raw.length - 4))}${raw.slice(-4)}`;
  }, [form.tin]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!affiliateUserId) {
        setStatusLoading(false);
        return;
      }
      try {
        setStatusLoading(true);
        const res = await affiliateService.getTaxStatus(affiliateUserId);
        if (!mounted) return;
        setStatus(res);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load tax status');
      } finally {
        if (mounted) setStatusLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [affiliateUserId]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'tin') {
      setForm((prev) => ({ ...prev, tin: digitsOnly(value).slice(0, 9) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (): string | null => {
    if (!affiliateUserId) return 'Not authenticated as affiliate.';
    if (!form.name?.trim()) return 'Name is required.';
    if (form.formType === 'W-9') {
      if (!form.tin || digitsOnly(form.tin).length < 9) return 'Valid SSN/EIN (9 digits) is required for W-9.';
      if (!form.addressLine1?.trim()) return 'Address Line 1 is required.';
      if (!form.city?.trim()) return 'City is required.';
      if (!form.state?.trim()) return 'State/Province is required.';
      if (!form.postalCode?.trim()) return 'Postal code is required.';
    }
    if (form.formType === 'W-8BEN') {
      if (!form.country?.trim()) return 'Country is required for W-8BEN.';
      // TIN optional for W-8BEN
    }
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setLoading(true);
      // Prepare sanitized payload; backend encrypts and stores securely
      const payload = {
        name: form.name?.trim(),
        business_name: form.businessName?.trim() || undefined,
        tin: digitsOnly(form.tin || ''),
        country: form.country?.trim() || undefined,
        address_line1: form.addressLine1?.trim() || undefined,
        address_line2: form.addressLine2?.trim() || undefined,
        city: form.city?.trim() || undefined,
        state: form.state?.trim() || undefined,
        postal_code: form.postalCode?.trim() || undefined
      };
      const res = await affiliateService.submitTaxForm(affiliateUserId!, form.formType, payload);
      if (res?.success) {
        toast.success('Tax form submitted');
        // refresh status
        const s = await affiliateService.getTaxStatus(affiliateUserId!);
        setStatus(s);
      } else {
        toast.error('Submission failed');
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to submit tax form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white shadow rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <h1 className="text-xl font-semibold text-gray-900">Tax & Compliance</h1>
        <p className="text-sm text-gray-500 mt-1">Submit your W-9 or W-8BEN form. Sensitive data is encrypted and stored securely.</p>
      </div>

      {/* Status */}
      <div className="px-6 py-4">
        {statusLoading ? (
          <div className="text-gray-500">Loading status...</div>
        ) : status?.has_submission ? (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
            Latest submission status: <strong>{status?.latest?.status || 'submitted'}</strong>
            {status?.latest?.last4 ? (<span className="ml-2">• TIN ending {status.latest.last4}</span>) : null}
            {status?.latest?.form_type ? (<span className="ml-2">• {status.latest.form_type}</span>) : null}
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
            No tax form on file. Please submit to enable payouts.
          </div>
        )}
      </div>

      {/* Form */}
      <form onSubmit={onSubmit} className="px-6 pb-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Form Type</label>
          <select
            name="formType"
            value={form.formType}
            onChange={(e) => setForm((p) => ({ ...p, formType: e.target.value as TaxFormType }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="W-9">W-9 (US Person)</option>
            <option value="W-8BEN">W-8BEN (Non-US)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={onChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Business Name (optional)</label>
          <input
            type="text"
            name="businessName"
            value={form.businessName}
            onChange={onChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>

        {form.formType === 'W-9' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">SSN / EIN</label>
            <input
              type="text"
              name="tin"
              value={maskedTin}
              onChange={onChange}
              onFocus={(e) => {
                // unmask on focus by setting input value to raw digits
                (e.target as HTMLInputElement).value = digitsOnly(form.tin || '');
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Digits only"
              inputMode="numeric"
              maxLength={11}
            />
          </div>
        )}

        {form.formType === 'W-8BEN' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Country</label>
            <input
              type="text"
              name="country"
              value={form.country}
              onChange={onChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Country of residence"
            />
          </div>
        )}

        {/* Address */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
            <input
              type="text"
              name="addressLine1"
              value={form.addressLine1}
              onChange={onChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
            <input
              type="text"
              name="addressLine2"
              value={form.addressLine2}
              onChange={onChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">City</label>
            <input
              type="text"
              name="city"
              value={form.city}
              onChange={onChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">State/Province</label>
            <input
              type="text"
              name="state"
              value={form.state}
              onChange={onChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Postal Code</label>
            <input
              type="text"
              name="postalCode"
              value={form.postalCode}
              onChange={onChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading || !affiliateUserId}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? 'Submitting...' : 'Submit Tax Form'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AffiliateTaxCompliance;
